import React, { useState, useEffect } from 'react';
import { Modal, Spin, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import {
    WarningOutlined,
    ClockCircleOutlined,
    MessageOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    FireOutlined,
    CalendarOutlined,
    UserDeleteOutlined,
    FieldTimeOutlined,
    PaperClipOutlined,
    StopOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { getSocket } from '@/services/socketClient';

// â”€â”€ Static maps â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const ICON_MAP = {
    warning: <WarningOutlined />,
    workload: <InfoCircleOutlined />,
    clock: <ClockCircleOutlined />,
    message: <MessageOutlined />,
    info: <InfoCircleOutlined />,
    fire: <FireOutlined />,
    calendar: <CalendarOutlined />,
    unassigned: <UserDeleteOutlined />,
    time: <FieldTimeOutlined />,
    attachment: <PaperClipOutlined />,
    stop: <StopOutlined />,
};

// Map backend severity directly to our styles
const SEVERITY_STYLES = {
    Critical: { wrapper: 'bg-red-50 border border-red-200 dark:bg-[#1f1f1f] dark:border-[#303030]', iconClass: 'text-red-500', headerBg: 'bg-red-50 border-red-200 text-red-800' },
    High: { wrapper: 'bg-orange-50 border border-orange-200 dark:bg-[#1f1f1f] dark:border-[#303030]', iconClass: 'text-orange-500', headerBg: 'bg-orange-50 border-orange-200 text-orange-800' },
    Medium: { wrapper: 'bg-amber-50 border border-amber-200 dark:bg-[#1f1f1f] dark:border-[#303030]', iconClass: 'text-amber-500', headerBg: 'bg-amber-50 border-amber-200 text-amber-800' },
    Low: { wrapper: 'bg-blue-50 border border-blue-200 dark:bg-[#1f1f1f] dark:border-[#303030]', iconClass: 'text-blue-500', headerBg: 'bg-blue-50 border-blue-200 text-blue-800' },
    Information: { wrapper: 'bg-slate-50 border border-slate-200 dark:bg-[#1f1f1f] dark:border-[#303030]', iconClass: 'text-slate-400', headerBg: 'bg-slate-50 border-slate-200 text-slate-800' },
};

const CATEGORY_ICON_MAP = {
    'Task Execution': 'calendar',
    'Time Tracking': 'time',
    'Feedback': 'message',
    'Review': 'clock',
    'Resource': 'workload',
    'AI Recommendation': 'info'
};

const STATUS_DOT = {
    Backlog: 'bg-slate-300', 'In Progress': 'bg-[#28b5fd]', 'Under Review': 'bg-amber-400',
    Completed: 'bg-emerald-400', Closed: 'bg-slate-400', Close: 'bg-slate-400', 'On Hold': 'bg-orange-400', Cancelled: 'bg-red-400',
};
const PRIORITY_STYLE = {
    Critical: 'bg-[#ffdad6] text-[#93000a]', High: 'bg-red-50 text-red-600',
    Medium: 'bg-[#c9e6ff] text-[#004c6e]', Low: 'bg-[#e7e8e9] text-[#424750]',
};

// â”€â”€ AlertTasksModal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function AlertTasksModal({ alert, open, onClose, onTaskClick, onProjectClick, onStatusUpdate }) {
    // Hooks must be called unconditionally â€” before any early return
    const [actionLoading, setActionLoading] = useState(null);

    if (!alert) return null;
    const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.Information;
    const iconKey = CATEGORY_ICON_MAP[alert.category] || 'warning';

    const handleAction = async (status) => {
        try {
            await onStatusUpdate(alert._id, status);
            onClose();
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Dispatches an AI recommended action button click.
     * Client-only actions are handled immediately without a network call.
     * Server-side actions POST to /api/project-management/ai-alerts/:id/action.
     */
    const handleActionClick = async (act) => {
        const { actionType } = act;

        // â”€â”€ Client-only actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        if (actionType === 'VIEW_TASK') {
            if (alert.relatedTask) {
                onTaskClick(alert.relatedTask);
                onClose();
            } else {
                message.warning('No task linked to this alert.');
            }
            return;
        }

        // Navigate to the project in the PM dashboard
        if (actionType === 'VIEW_PROJECT') {
            const projectId = alert.relatedProject?._id || alert.relatedProject;
            if (projectId) {
                onProjectClick(projectId);
                onClose();
            } else {
                message.warning('No project linked to this alert.');
            }
            return;
        }

        if (['VIEW_WORKLOAD', 'REASSIGN_TASK', 'RESOLVE_BLOCKER',
            'VIEW_COMMENTS', 'ASSIGN_TASK', 'ASSIGN_REVIEWER', 'ADD_COMMENT',
            'REVIEW_EFFORT', 'VIEW_TIME_LOG'].includes(actionType)) {
            if (alert.relatedTask) {
                onTaskClick(alert.relatedTask);
                onClose();
            } else {
                message.info('Open the task to take this action.');
            }
            return;
        }

        // â”€â”€ Server-side actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        // NOTIFY_ASSIGNEE, REQUEST_UPDATE, PING_REVIEWER, ESCALATE_PRIORITY,
        // NOTIFY_PROJECT_MANAGER all POST to the backend action endpoint.
        setActionLoading(actionType);
        try {
            const { data } = await axios.post(
                `project-management/ai-alerts/${alert._id}/action`,
                { actionType }
            );
            if (data.success) {
                message.success(data.message || 'Action completed.');
                // Close modal when the action fully resolves the alert
                if (['ESCALATE_PRIORITY', 'INCREASE_PRIORITY', 'NOTIFY_PROJECT_MANAGER'].includes(actionType)) {
                    onClose();
                }
            } else {
                message.error(data.message || 'Action failed.');
            }
        } catch (err) {
            message.error(err?.response?.data?.message || 'Action failed.');
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={640}
            centered
            closable={false}
            destroyOnClose
            styles={{ content: { padding: 0, borderRadius: 16, overflow: 'hidden' }, body: { padding: 0 } }}
        >
            <div className="flex flex-col">
                {/* Header */}
                <div className={`px-6 py-4 border-b flex items-start justify-between gap-4 ${style.headerBg}`}>
                    <div className="flex items-start gap-3">
                        <span className={`${style.iconClass} flex-shrink-0 mt-0.5`} style={{ fontSize: 18 }}>
                            {ICON_MAP[iconKey]}
                        </span>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-base font-black font-headline m-0">{alert.title}</h3>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/50 border border-black/10`}>
                                    {alert.severity}
                                </span>
                            </div>
                            <p className="text-sm mt-0.5 opacity-90 m-0">{alert.message}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/10 transition-colors flex-shrink-0" aria-label="Close">
                        <span className="material-symbols-outlined text-base">close</span>
                    </button>
                </div>

                {/* Related Entity Details */}
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#f3f4f5] dark:divide-slate-700">
                    {alert.relatedTask ? (
                        <div
                            onClick={() => { onTaskClick(alert.relatedTask); onClose(); }}
                            className="flex items-center gap-4 px-6 py-4 hover:bg-[#f8f9fa] dark:hover:bg-[#1e293b]/60 cursor-pointer transition-colors"
                        >
                            <span className={`w-3 h-3 rounded-full flex-shrink-0 ${STATUS_DOT[alert.relatedTask.status] || 'bg-slate-300'}`} />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-[#191c1d] dark:text-white truncate m-0 mb-1">{alert.relatedTask.title}</p>
                                <p className="text-xs text-[#737782] dark:text-slate-400 m-0 font-mono">{alert.relatedTask.taskId}</p>
                            </div>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${PRIORITY_STYLE[alert.relatedTask.priority] || PRIORITY_STYLE.Medium}`}>
                                {alert.relatedTask.priority || 'Medium'}
                            </span>
                            <RightOutlined className="text-[#c2c6d2] flex-shrink-0" style={{ fontSize: 12 }} />
                        </div>
                    ) : (
                        <div className="px-6 py-6 text-sm text-center text-[#737782]">No specific task associated with this alert.</div>
                    )}
                </div>

                {/* AI Actions */}
                {alert.actions && alert.actions.length > 0 && (
                    <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-[#e7e8e9] dark:border-slate-700">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">AI Recommended Actions</p>
                        <div className="flex flex-wrap gap-2">
                            {alert.actions.map((act, i) => (
                                <button
                                    key={i}
                                    onClick={() => handleActionClick(act)}
                                    disabled={actionLoading !== null}
                                    className="px-3 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                >
                                    {actionLoading === act.actionType && (
                                        <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin inline-block" />
                                    )}
                                    {act.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Footer Lifecycle Actions */}
                <div className="px-6 py-4 border-t border-[#e7e8e9] dark:border-slate-700 flex items-center justify-between bg-white dark:bg-[#1e293b]">
                    <div className="flex gap-2">
                        {alert.status === 'New' && (
                            <button onClick={() => handleAction('Acknowledged')} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm">
                                Acknowledge
                            </button>
                        )}
                        {['New', 'Acknowledged', 'In Progress'].includes(alert.status) && (
                            <button onClick={() => handleAction('Resolved')} className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-all shadow-sm">
                                Mark Resolved
                            </button>
                        )}
                    </div>
                    <button onClick={() => handleAction('Dismissed')} className="px-4 py-2 text-sm font-semibold text-[#424750] dark:text-slate-300 hover:bg-[#e7e8e9] dark:hover:bg-[#334155] rounded-lg transition-all">
                        Dismiss
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// â”€â”€ Main component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export default function AIAlertsPanel({ onTaskClick, projectId = null, pendingAlertId = null, onPendingAlertConsumed }) {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const history = useHistory();

    // Passed to AlertTasksModal so VIEW_PROJECT can navigate
    const handleProjectClick = (pid) => {
        history.push({ pathname: '/project-management', state: { projectId: pid } });
    };

    const fetchAlerts = async () => {
        try {
            const url = projectId
                ? `project-management/ai-alerts?projectId=${projectId}`
                : `project-management/ai-alerts`;
            const { data } = await axios.get(url);
            if (data.success) {
                setAlerts(data.alerts || []);
            }
        } catch (error) {
            console.error("Failed to fetch AI alerts", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();

        const socket = getSocket();
        if (socket) {
            socket.on('ai_alert:updated', (newAlert) => {
                setAlerts((prev) => {
                    // Update if exists, otherwise add to front
                    const exists = prev.find(a => a._id === newAlert._id);
                    if (exists) {
                        return prev.map(a => a._id === newAlert._id ? newAlert : a);
                    }
                    return [newAlert, ...prev];
                });
            });
        }

        return () => {
            if (socket) {
                socket.off('ai_alert:updated');
            }
        };
    }, [projectId]);

    // â”€â”€ Deep-link: auto-open the modal when a pendingAlertId is passed in â”€â”€â”€
    // Runs whenever pendingAlertId or the alerts list changes. Once the target
    // alert is found and the modal is opened, we tell the parent to clear the
    // pending ID so the modal doesn't reopen on every subsequent render.
    useEffect(() => {
        if (!pendingAlertId || loading || alerts.length === 0) return;
        const target = alerts.find(a => a._id === pendingAlertId);
        if (target) {
            setSelectedAlert(target);
            setModalOpen(true);
            onPendingAlertConsumed?.();
        }
    }, [pendingAlertId, alerts, loading]);

    const handleAlertClick = (alert) => {
        setSelectedAlert(alert);
        setModalOpen(true);
    };

    const handleStatusUpdate = async (alertId, status) => {
        try {
            const { data } = await axios.put(`project-management/ai-alerts/${alertId}/status`, { status });
            if (data.success) {
                message.success(`Alert marked as ${status}`);
                // Optimistically remove or update from list
                if (['Resolved', 'Dismissed'].includes(status)) {
                    setAlerts(prev => prev.filter(a => a._id !== alertId));
                } else {
                    setAlerts(prev => prev.map(a => a._id === alertId ? { ...a, status } : a));
                }
            }
        } catch (error) {
            message.error("Failed to update alert status");
            throw error;
        }
    };

    return (
        <>
            <div className="flex-1 flex flex-col min-h-0 h-full w-full">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-black text-[#003263] dark:text-[#a7c8ff] font-headline m-0">AI Smart Alerts</h2>
                        {alerts.length > 0 && (
                            <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                {alerts.length}
                            </span>
                        )}
                    </div>
                    <span className="bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded uppercase tracking-widest flex items-center gap-1 shadow-sm">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                        Engine Active
                    </span>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pr-1 custom-scrollbar">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <Spin />
                        </div>
                    ) : alerts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                                <CheckCircleOutlined className="text-emerald-500 text-xl" />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-700 dark:text-slate-300 m-0">All Clear</p>
                                <p className="text-xs text-slate-500 mt-1 m-0">No active execution risks detected.</p>
                            </div>
                        </div>
                    ) : (
                        alerts.map((alert) => {
                            const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.Information;
                            const iconKey = CATEGORY_ICON_MAP[alert.category] || 'warning';

                            return (
                                <div
                                    key={alert._id}
                                    onClick={() => handleAlertClick(alert)}
                                    className={`rounded-xl ${style.wrapper} flex flex-col p-3.5 transition-all duration-200 cursor-pointer hover:shadow-md active:scale-[0.98] group`}
                                >
                                    <div className="flex gap-3">
                                        <div className={`w-8 h-8 rounded-full bg-white/60 dark:bg-black/20 flex items-center justify-center flex-shrink-0 shadow-sm ${style.iconClass}`}>
                                            {ICON_MAP[iconKey]}
                                        </div>
                                        <div className="flex-1 min-w-0 pt-0.5">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate m-0">
                                                    {alert.title}
                                                </h4>
                                                <RightOutlined className="text-slate-300 group-hover:text-slate-500 transition-colors text-[10px]" />
                                            </div>
                                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 line-clamp-2 m-0 leading-relaxed">
                                                {alert.message}
                                            </p>

                                            {/* Footer Info inside card */}
                                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5 dark:border-white/5">
                                                {alert.relatedTask ? (
                                                    <span className="text-[10px] font-bold text-slate-500 truncate pr-2">
                                                        Re: {alert.relatedTask.title}
                                                    </span>
                                                ) : <span />}

                                                <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-white/50 border border-black/5`}>
                                                    {alert.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            <AlertTasksModal
                alert={selectedAlert}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onTaskClick={onTaskClick || (() => { })}
                onProjectClick={handleProjectClick}
                onStatusUpdate={handleStatusUpdate}
            />
        </>
    );
}


